import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import React from "react";
import styled from "styled-components";

import { useBulkAnalyzeClaimsMutation } from "../../store/queries/claims";
import { ClaimBulkTarget } from "../../types/claim";
import {
  bulkErrorDetail,
  claimCount,
  useBulkActionToast,
} from "./bulkActionShared";

interface IBulkRunAiAnalysisModalProps {
  visible: boolean;
  onHide: () => void;
  /** The selected rows, or every claim matching the list's filters (CLE-3035). */
  target: ClaimBulkTarget;
  /** How many claims `target` covers — for the copy only. */
  count: number;
  onSuccess: () => void;
}

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 28rem;
`;

const Hint = styled.div`
  color: #6c757d;
  font-size: 0.875rem;
`;

/**
 * CLE-2551 / CLE-3035: confirm modal for running AI analysis on any number of
 * claims. The endpoint answers 202 (it clears the existing analyses at once);
 * the analyses run in the background on the EDI worker and the banner above
 * the claims list shows their progress.
 */
export function BulkRunAiAnalysisModal({
  visible,
  onHide,
  target,
  count,
  onSuccess,
}: IBulkRunAiAnalysisModalProps) {
  const { localToast, show } = useBulkActionToast();
  const [bulkAnalyze, { isLoading: isStarting }] =
    useBulkAnalyzeClaimsMutation();

  const handleApply = async () => {
    try {
      await bulkAnalyze(target).unwrap();
      show({
        severity: "success",
        summary: "AI analysis started",
        detail: `AI analysis started for ${claimCount(
          count
        )}. It runs in the background — progress shows above the list and we'll let you know here when it finishes.`,
        life: 8000,
      });
      onSuccess();
    } catch (err: any) {
      show({
        severity: "error",
        summary: "Could not start AI analysis",
        detail: bulkErrorDetail(err),
        life: 8000,
      });
    }
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button
        label="Cancel"
        className="p-button-text"
        onClick={onHide}
        disabled={isStarting}
      />
      <Button
        label={`Run on ${count.toLocaleString("en-US")}`}
        icon="pi pi-sparkles"
        onClick={handleApply}
        disabled={isStarting}
        loading={isStarting}
      />
    </div>
  );

  return (
    <>
      <Toast ref={localToast} />
      <Dialog
        header="Run AI analysis"
        visible={visible}
        onHide={isStarting ? () => {} : onHide}
        closable={!isStarting}
        footer={footer}
      >
        <Body>
          <div>
            Run AI analysis on <strong>{claimCount(count)}</strong>. Any
            existing analysis on these claims is replaced.
          </div>
          <Hint>
            This runs in the background — you can keep working. Progress shows
            above the claims list.
          </Hint>
        </Body>
      </Dialog>
    </>
  );
}
